import { createFeatureSelector, createSelector } from '@ngrx/store';
import { vehicleDetailsOverviewFeatureKey } from '@pages/shared-pages/vehicle-details-overview/store/vehicle-details-overview.reducer';
import { selectVehicleDetails } from '@pages/shared-pages/vehicle-details-overview/store/vehicle-details-overview.selectors';
import { singleSiteChargerDetailsFeatureKey } from '@pages/single-site/pages/single-site-charger-details/store/single-site-charger-details.reducer';
import { selectChargerName } from '@pages/single-site/pages/single-site-charger-details/store/single-site-charger-details.selectors';
import { sitesFeatureKey } from '@pages/sites/store/sites.reducer';
import { selectSites } from '@pages/sites/store/sites.selectors';

import { getConversationTitle } from '@domain/ai-assistant/conversation-titles';

import { AiAssistantState, aiAssistantFeatureKey } from './ai-assistant.reducer';

const ifFeatureLoaded =
  <T>(featureKey: string, selector: (state: object) => T) =>
  (state: Record<string, unknown>): T | undefined =>
    featureKey in state ? selector(state) : undefined;

export const selectSitesIfLoaded = ifFeatureLoaded(sitesFeatureKey, selectSites);
export const selectChargerNameIfLoaded = ifFeatureLoaded(singleSiteChargerDetailsFeatureKey, selectChargerName);
export const selectVehicleDetailsIfLoaded = ifFeatureLoaded(vehicleDetailsOverviewFeatureKey, selectVehicleDetails);

export const selectAiAssistantFeature = createFeatureSelector<AiAssistantState>(aiAssistantFeatureKey);

export const selectPanelSize = createSelector(selectAiAssistantFeature, (s) => s.panelSize);
export const selectIsPanelOpen = createSelector(selectPanelSize, (size) => size !== 'closed');
export const selectIsFullscreen = createSelector(selectPanelSize, (size) => size === 'fullscreen');

export const selectConversations = createSelector(selectAiAssistantFeature, (s) => s.conversations);
export const selectConversationsLoading = createSelector(selectAiAssistantFeature, (s) => s.conversationsLoading);
export const selectConversationsTotal = createSelector(selectAiAssistantFeature, (s) => s.conversationsTotal);
export const selectConversationsHasMore = createSelector(
  selectConversations,
  selectConversationsTotal,
  (conversations, total) => conversations.length < total
);
export const selectActiveConversationId = createSelector(selectAiAssistantFeature, (s) => s.activeConversationId);

export const selectConversationListVm = createSelector(selectConversations, selectActiveConversationId, (conversations, activeId) =>
  conversations.map((c) => ({
    id: c.id,
    title: getConversationTitle(c.id, c.name),
    isActive: c.id === activeId,
  }))
);

export const selectRecentConversations = createSelector(selectAiAssistantFeature, (s) => s.recentConversations);
export const selectRecentConversationsLoading = createSelector(selectAiAssistantFeature, (s) => s.recentConversationsLoading);
export const selectPromptPresets = createSelector(selectAiAssistantFeature, (s) => s.promptPresets);
export const selectPromptPresetsLoading = createSelector(selectAiAssistantFeature, (s) => s.promptPresetsLoading);

export const selectRecentConversationListVm = createSelector(
  selectRecentConversations,
  selectActiveConversationId,
  (conversations, activeId) =>
    conversations.map((c) => ({
      id: c.id,
      title: getConversationTitle(c.id, c.name),
      isActive: c.id === activeId,
    }))
);

export const selectMessages = createSelector(selectAiAssistantFeature, (s) => s.messages);
export const selectMessagesLoading = createSelector(selectAiAssistantFeature, (s) => s.messagesLoading);
export const selectStatus = createSelector(selectAiAssistantFeature, (s) => s.status);
export const selectIsStreaming = createSelector(selectAiAssistantFeature, (s) => s.isStreaming);
export const selectPendingConsent = createSelector(selectAiAssistantFeature, (s) => s.pendingConsent);
export const selectCreatingConversation = createSelector(selectAiAssistantFeature, (s) => s.creatingConversation);

export const selectComposerDisabled = createSelector(
  selectIsStreaming,
  selectStatus,
  selectCreatingConversation,
  (isStreaming, status, creating) => isStreaming || status === 'WAITING_FOR_TOOL_CONSENT' || creating
);

export const selectStreamError = createSelector(selectAiAssistantFeature, (s) => s.streamError);

export const selectIsNewChat = createSelector(
  selectActiveConversationId,
  selectMessages,
  (activeId, messages) => activeId === undefined && messages.length === 0
);

export const selectShowTypingIndicator = createSelector(selectIsStreaming, selectMessages, (isStreaming, messages) => {
  if (!isStreaming) return false;
  const last = messages.at(-1);
  if (!last || last.role !== 'assistant') return true;
  const lastPart = last.parts.at(-1);
  return !lastPart || lastPart.complete;
});
